import {
  Form,
  FormSubmitButton,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useCreateMassTort } from "@/lib/data/react-query/mass-torts/mass-tort";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  massTortTitle: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(500, { message: "Title must be 500 characters or less" }),
  massTortDescription: z
    .string()
    .max(5000, { message: "Description must be 5,000 characters or less" }),
});

type CreateMassTortFormValues = z.infer<typeof formSchema>;

const defaultFormValues: CreateMassTortFormValues = {
  massTortTitle: "",
  massTortDescription: "",
};

const CREATE_MASS_TORT_FORM_ID = "create-mass-tort-form";

interface CreateMassTortSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function CreateMassTortSlideover({
  open,
  setOpen,
}: CreateMassTortSlideoverProps) {
  const form = useForm<CreateMassTortFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const createMassTort = useCreateMassTort();
  const isFormLoading = createMassTort.isPending;

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
    }
  }, [open, form]);

  const onSubmit = (data: CreateMassTortFormValues) => {
    const description = data.massTortDescription.trim();
    createMassTort.mutate(
      {
        title: data.massTortTitle.trim(),
        description: description ? description : null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          form.reset(defaultFormValues);
        },
      },
    );
  };

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Create mass tort"
        description="Enter a title and optional description for this mass tort campaign."
        size="xl"
        isDirty={form.formState.isDirty}
        loading={isFormLoading}
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              isDirty={form.formState.isDirty}
              setOpen={setOpen}
            />
            <FormSubmitButton
              title="Create mass tort"
              loading={isFormLoading}
              submitFormId={CREATE_MASS_TORT_FORM_ID}
            />
          </div>
        }
      >
        <form
          id={CREATE_MASS_TORT_FORM_ID}
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(onSubmit)();
          }}
        >
          <fieldset className={cn(slideoverFieldsetClassName, "my-0 p-0")}>
            <TextFormField
              register={form.register("massTortTitle")}
              label="Title*"
            />
            <div className="col-span-2">
              <TextAreaFormField
                register={form.register("massTortDescription")}
                label="Description"
              />
            </div>
          </fieldset>
        </form>
      </AppModal>
    </Form>
  );
}
